import { useEffect, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconClock,
  IconHeart,
  IconHome,
  IconLock,
  IconMapPin,
  IconUsers,
} from '@tabler/icons-react';
import { ProperPreSaleVisit } from './models/dashboardModels';
import { VisitPreSaleState } from '../../constants/visitsConstants';
import {
  getBadgeColorVisitPreSaleStatusAdapter,
  getVisitPreSaleStatusAdapter,
} from '../properties/adapters/visitsAdapter';
import { Field, Section } from './components/VisitDetailParts';
import usePreSaleVisitActions, {
  ProjectModelOption,
} from './hooks/usePreSaleVisitActions';
import IntentSignalsCell from './components/IntentSignalsCell';
import PreSaleVisitFeedbackModal, {
  PreSaleVisitFeedback,
} from './components/PreSaleVisitFeedbackModal';
import usePreSaleVisitFeedback from './hooks/usePreSaleVisitFeedback';
import PreSaleFeedbackCard from './components/PreSaleFeedbackCard';
import ProperCancelPanel from './components/ProperCancelPanel';
import InvestorFavoritesModal from './components/InvestorFavoritesModal';
import VisitMarketplaceTypeBadge from '../../components/VisitMarketplaceTypeBadge';

interface LocationState {
  visit?: ProperPreSaleVisit;
}

const STEPS = [
  { key: VisitPreSaleState.PENDING_APPROVAL, label: 'Pendiente de aprobación' },
  { key: VisitPreSaleState.SCHEDULED, label: 'Programada' },
  { key: VisitPreSaleState.DONE, label: 'Finalizada' },
];

const getCurrentStep = (status: string): number => {
  switch (status) {
    case VisitPreSaleState.PENDING_APPROVAL:
      return 0;
    case VisitPreSaleState.SCHEDULED:
    case VisitPreSaleState.RESCHEDULED:
      return 1;
    case VisitPreSaleState.DONE:
      return 2;
    default:
      return -1;
  }
};

// Detalle de una asesoría de PreVenta para el rol Proper. Sólo usa los
// endpoints que el Proper ya utiliza en su dashboard: feedback, marcar como
// realizada y cancelar.
const ProperPreSaleVisitDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const visit = (location.state as LocationState | null)?.visit;

  const {
    cancellationReasons,
    doMarkAsDone,
    doCancelVisit,
    doLoadCancellationReasons,
    doLoadProjectModels,
    doSendFeedback,
  } = usePreSaleVisitActions();

  const savedFeedback = usePreSaleVisitFeedback(
    visit?.id,
    visit?.visitInfo.status === VisitPreSaleState.DONE,
  );
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [projectModels, setProjectModels] = useState<ProjectModelOption[]>([]);

  useEffect(() => {
    if (visit?.visitInfo.status === VisitPreSaleState.DONE) {
      doLoadProjectModels(visit.projectInfo.id).then(setProjectModels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  useEffect(() => {
    if (visit) doLoadCancellationReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  const goBack = () => navigate(-1);

  if (!visit) {
    return (
      <Container className="mt-6 visit-detail-v2" fluid>
        <Button className="vd-back" onClick={goBack}>
          <IconArrowLeft size={16} /> Volver a visitas
        </Button>
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se encontró la asesoría. Ábrela desde la lista de PreVenta del
            dashboard.
          </p>
        </section>
      </Container>
    );
  }

  const { visitInfo, projectInfo } = visit;
  const status = visitInfo.status;
  const currentStep = getCurrentStep(status);
  const canMarkDone = [
    VisitPreSaleState.SCHEDULED,
    VisitPreSaleState.RESCHEDULED,
  ].includes(status);

  const isDone = status === VisitPreSaleState.DONE;
  const modelName = projectModels.find(
    (m) => m.id === savedFeedback.feedback?.project_model_id,
  )?.name;

  const openFeedback = async () => {
    setFeedbackError('');
    setCancelOpen(false);
    setProjectModels([]);
    setFeedbackOpen(true);
    setProjectModels(await doLoadProjectModels(projectInfo.id));
  };

  const submitFeedbackOnly = async (feedback: PreSaleVisitFeedback) => {
    setFeedbackSaving(true);
    setFeedbackError('');
    try {
      const result = await doSendFeedback(visit.id, feedback);
      if (!result.ok) {
        setFeedbackError(result.message || 'No se pudo guardar el feedback.');
        return;
      }
      setFeedbackOpen(false);
      savedFeedback.reload();
    } finally {
      setFeedbackSaving(false);
    }
  };

  const submitFeedback = async (feedback: PreSaleVisitFeedback) => {
    setFeedbackSaving(true);
    setFeedbackError('');
    try {
      const result = await doSendFeedback(visit.id, feedback);
      if (!result.ok) {
        setFeedbackError(result.message || 'No se pudo guardar el feedback.');
        return;
      }
      if ((await doMarkAsDone(visit.id)) === 200) goBack();
      else
        setFeedbackError(
          'El feedback se guardó, pero no se pudo marcar la visita como realizada. Intenta de nuevo.',
        );
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleCancel = async (
    reason: string,
    explain: string,
    voided: boolean,
  ) => {
    setCancelError('');
    const result = await doCancelVisit(
      visit.id,
      reason,
      explain,
      voided ? VisitPreSaleState.VOIDED : '',
    );
    if (result === 200) goBack();
    else setCancelError(`(${result}) No fue posible cancelar la visita`);
  };

  return (
    <Container className="mt-6 visit-detail-v2" fluid>
      <Button className="vd-back" onClick={goBack}>
        <IconArrowLeft size={16} /> Volver a visitas
      </Button>

      <section className="vd-card vd-hero">
        <div className="vd-hero__top">
          <span className="vd-icon vd-icon--green vd-icon--lg">
            <IconHome size={26} />
          </span>
          <div>
            <div className="vd-hero__title-row">
              <h1 className="vd-hero__title">{projectInfo.name}</h1>
              <span
                className={`vd-pill vd-pill--${getBadgeColorVisitPreSaleStatusAdapter(
                  status,
                )}`}
              >
                {getVisitPreSaleStatusAdapter(status)}
              </span>
            </div>
            <div className="vd-hero__sub">
              <IconMapPin size={14} /> {projectInfo.address}
            </div>
            <div className="vd-hero__code">
              [{projectInfo.code}] · {visit.id}
            </div>
          </div>
        </div>

        {canMarkDone && (
          <div className="vd-actions">
            <button
              type="button"
              className="vd-btn vd-btn--primary"
              onClick={openFeedback}
            >
              <IconCheck size={16} /> Marcar como realizada
            </button>
            <button
              type="button"
              className="vd-btn vd-btn--danger"
              onClick={() => setCancelOpen((open) => !open)}
              aria-expanded={cancelOpen}
            >
              Cancelar
            </button>
          </div>
        )}

        {canMarkDone && cancelOpen && (
          <ProperCancelPanel
            title="Cancelar visita"
            reasons={cancellationReasons}
            allowVoided
            error={cancelError}
            onConfirm={handleCancel}
            onDiscard={() => {
              setCancelOpen(false);
              setCancelError('');
            }}
          />
        )}
      </section>

      <div className="vd-layout">
        <div className="vd-col">
          <Section icon={<IconUsers size={20} />} tone="blue" title="Cliente">
            <div className="vd-grid">
              <Field label="Nombre">{visitInfo.investor_name}</Field>
              <Field label="Correo">{visitInfo.investor_email}</Field>
              <Field label="Teléfono">{visitInfo.investor_phone}</Field>
              <Field label="Origen">
                <VisitMarketplaceTypeBadge
                  marketplace_type={visitInfo.marketplace_type}
                />
              </Field>
            </div>
          </Section>

          <Section icon={<IconCalendar size={20} />} tone="green" title="Visita">
            <div className="vd-grid">
              <Field label="Fecha">{visitInfo.visit_date}</Field>
              <Field label="Hora">{visitInfo.visit_time}</Field>
            </div>
            <div className="vd-signals">
              <span className="vd-field__label">Señales de intención</span>
              <IntentSignalsCell
                favorites={visitInfo.investor_favorites}
                visitDate={visitInfo.visit_date}
                visitTime={visitInfo.visit_time}
                status={status}
                isPreSale
                onViewFavorites={() => setFavoritesOpen(true)}
              />
              {(visitInfo.investor_favorites?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className="vd-link"
                  onClick={() => setFavoritesOpen(true)}
                >
                  <IconHeart size={14} /> Ver favoritos
                </button>
              )}
            </div>
          </Section>
        </div>

        <div className="vd-col">
          <Section
            icon={<IconClock size={20} />}
            tone="orange"
            title="Estado de la visita"
          >
            {currentStep === -1 ? (
              <p className="mb-0">
                Esta asesoría está{' '}
                <strong>
                  {getVisitPreSaleStatusAdapter(status).toLowerCase()}
                </strong>
                .
              </p>
            ) : (
              <ol
                className={`vd-steps vd-steps--${getBadgeColorVisitPreSaleStatusAdapter(
                  status,
                )}`}
              >
                {STEPS.map((step, index) => (
                  <li
                    key={step.key}
                    className={`vd-step${
                      index < currentStep ? ' is-done' : ''
                    }${index === currentStep ? ' is-current' : ''}`}
                  >
                    <span className="vd-step__dot">
                      {index < currentStep ? <IconCheck size={12} /> : index + 1}
                    </span>
                    <span className="vd-step__text">
                      <span className="vd-step__label">{step.label}</span>
                      {index === currentStep && (
                        <span className="vd-step__current">Estado actual</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {isDone && (
            <>
              <PreSaleFeedbackCard
                state={savedFeedback.state}
                feedback={savedFeedback.feedback}
                modelName={modelName}
                onGiveFeedback={openFeedback}
              />
              <section className="vd-card vd-locked">
                <header className="vd-card__header">
                  <span className="vd-icon vd-icon--muted">
                    <IconLock size={20} />
                  </span>
                  <h2 className="vd-card__title">Reporte del propietario</h2>
                </header>
                <p className="vd-locked__text">
                  Para generar el reporte necesitamos el feedback del Proper y
                  del cliente.{' '}
                  {savedFeedback.state === 'done'
                    ? 'Falta la respuesta del cliente.'
                    : 'Falta el feedback del Proper y la respuesta del cliente.'}
                </p>
              </section>
            </>
          )}
        </div>
      </div>

      <PreSaleVisitFeedbackModal
        isOpen={feedbackOpen}
        projectName={projectInfo.name}
        models={projectModels.map((m) => ({ value: m.id, label: m.name }))}
        error={feedbackError}
        loading={feedbackSaving}
        onClose={() => setFeedbackOpen(false)}
        submitLabel={isDone ? 'Guardar feedback' : undefined}
        onSubmit={isDone ? submitFeedbackOnly : submitFeedback}
      />
      <InvestorFavoritesModal
        isOpen={favoritesOpen}
        toggle={() => setFavoritesOpen(false)}
        investorName={visitInfo.investor_name}
        favorites={visitInfo.investor_favorites || []}
        currentListingId={projectInfo.id}
      />
    </Container>
  );
};

export default ProperPreSaleVisitDetail;
